'use client'

import {
    approveTransfer,
    FinancialTransfer,
    getTransfers,
    rejectTransfer,
} from '@/lib/api/financial/transfer-api'
import PasswordField from '../payment-account/components/password-field'
import { useIsFinanceOperator } from '@/lib/hooks/use-is-finance-operator'
import { useEffect, useState } from 'react'
import { MdCheck, MdClose, MdInfoOutline, MdRefresh, MdVerifiedUser } from 'react-icons/md'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number): string =>
    (Math.abs(cents) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatDateTime = (iso: string): string =>
    new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })

export default function TransferApprovalsPage() {
    const [transfers, setTransfers] = useState<FinancialTransfer[]>([])
    const [loading, setLoading] = useState(true)
    const { isOperator, hasOperators, loading: loadingOperator } = useIsFinanceOperator()

    const [confirming, setConfirming] = useState<{ id: number; action: 'approve' | 'reject' } | null>(null)
    const [reason, setReason] = useState('')
    const [password, setPassword] = useState('')
    const [processing, setProcessing] = useState(false)

    useEffect(() => {
        load()
    }, [])

    const load = async () => {
        setLoading(true)
        try {
            const res = await getTransfers({ status: 'PENDING_APPROVAL' })
            if (res.success && res.result) setTransfers(res.result)
        } finally {
            setLoading(false)
        }
    }

    const handleConfirm = async () => {
        if (!confirming) return
        if (confirming.action === 'approve' && !password) {
            return toast.warning('Confirme sua senha para aprovar')
        }
        setProcessing(true)
        try {
            const res =
                confirming.action === 'approve'
                    ? await approveTransfer(confirming.id, password)
                    : await rejectTransfer(confirming.id, reason || 'Sem motivo informado')
            if (res.success) {
                toast.success(confirming.action === 'approve' ? 'Aprovada' : 'Rejeitada')
                setConfirming(null)
                setReason('')
                setPassword('')
                load()
            } else {
                toast.error(res.error || 'Erro ao processar')
            }
        } finally {
            setProcessing(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdVerifiedUser /> Aprovações de Transferência
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        Transferências aguardando aprovação. Aprovação dispara execução no provedor.
                    </p>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={load}>
                    <MdRefresh /> Atualizar
                </button>
            </div>

            {!loadingOperator && !isOperator && hasOperators && (
                <div className="alert alert-warning mb-4">
                    <MdInfoOutline className="text-xl" />
                    <span className="text-sm">
                        Apenas operadores financeiros eleitos podem aprovar ou rejeitar transferências. Você pode visualizar a lista mas não executar ações.
                    </span>
                </div>
            )}

            <div className="card bg-base-100 shadow">
                <div className="card-body p-0">
                    {loading && (
                        <div className="flex items-center justify-center min-h-[200px]">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                        </div>
                    )}
                    {!loading && transfers.length === 0 && (
                        <div className="text-center py-12 text-base-content/60">
                            Nenhuma transferência pendente de aprovação.
                        </div>
                    )}
                    {!loading && transfers.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Solicitada em</th>
                                        <th>Solicitante</th>
                                        <th>Destino</th>
                                        <th>Descrição</th>
                                        <th className="text-right">Valor</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transfers.map(t => (
                                        <tr key={t.id}>
                                            <td className="whitespace-nowrap">{formatDateTime(t.createdAt)}</td>
                                            <td>#{t.requestedById}</td>
                                            <td className="text-sm">
                                                {t.destinationType}
                                                {t.pixKey && <div className="text-xs text-base-content/60">PIX: {t.pixKey}</div>}
                                                {t.partnerId && <div className="text-xs text-base-content/60">Partner #{t.partnerId}</div>}
                                                {t.bankAccountId && <div className="text-xs text-base-content/60">BankAccount #{t.bankAccountId}</div>}
                                            </td>
                                            <td className="text-sm">{t.description || '—'}</td>
                                            <td className="text-right font-mono whitespace-nowrap text-error">
                                                {formatCurrency(t.amount)}
                                            </td>
                                            <td className="whitespace-nowrap">
                                                {isOperator ? (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-success btn-outline mr-1"
                                                            onClick={() => setConfirming({ id: t.id, action: 'approve' })}
                                                        >
                                                            <MdCheck /> Aprovar
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-error btn-outline"
                                                            onClick={() => setConfirming({ id: t.id, action: 'reject' })}
                                                        >
                                                            <MdClose /> Rejeitar
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-base-content/50">
                                                        Somente operadores
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {confirming?.action === 'approve' && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="font-bold text-lg mb-2">Aprovar transferência</h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            Ao aprovar, a transferência será executada imediatamente no provedor. Confirme sua senha para liberar.
                        </p>
                        <PasswordField value={password} onChange={setPassword} disabled={processing} />
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => { setConfirming(null); setPassword('') }}
                                disabled={processing}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={handleConfirm}
                                disabled={processing || !password}
                            >
                                {processing ? <span className="loading loading-spinner loading-sm" /> : 'Aprovar'}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {confirming?.action === 'reject' && (
                <dialog open className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">Rejeitar transferência</h3>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Motivo da rejeição</span>
                            <textarea
                                className="textarea textarea-bordered"
                                rows={3}
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                            />
                        </div>
                        <div className="modal-action">
                            <button className="btn btn-ghost" onClick={() => { setConfirming(null); setReason('') }} disabled={processing}>
                                Cancelar
                            </button>
                            <button className="btn btn-error" onClick={handleConfirm} disabled={processing || !reason.trim()}>
                                {processing ? <span className="loading loading-spinner loading-sm"></span> : 'Rejeitar'}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}
        </div>
    )
}
